/**
 * A saved delivery address, client side — the mirror of `backend/core/address.py`.
 *
 * The backend is the only real enforcement (`PUT /auth/profile` refuses a half-filled address);
 * this exists so the customer is told which field is missing *before* they press Save, and so the
 * three places that display a saved address — profile, cart, admin's person page — all compose it
 * the same way instead of each reaching for `a.address` and printing nothing for every customer
 * who saved one after the fields were added.
 *
 * Keep the rules here identical to the Python. Same arrangement as `lib/lifecycle.js`, which
 * mirrors `core/lifecycle.py`'s graphs for the admin dropdowns.
 */

export const DEFAULT_STATE = "Tamil Nadu";
const PINCODE_RE = /^[1-9][0-9]{5}$/;
const LABELS = { address1: "address line 1", city: "city", state: "state", pincode: "PIN code" };
const REQUIRED = ["address1", "city", "state", "pincode"];

const s = (a, k) => String((a || {})[k] ?? "").trim();

/** A blank address row, as Add leaves it. State is prefilled; almost nobody has to touch it. */
export const emptyAddress = () => ({
  label: "", address1: "", address2: "", city: "", state: DEFAULT_STATE, pincode: "",
});

/** Whether this entry uses the structured fields at all. */
export const isStructured = (a) =>
  ["address1", "address2", "city", "state", "pincode"].some((k) => s(a, k));

/**
 * An address saved in the old free-text shape, moved into the fields for editing.
 *
 * Its first two lines become lines 1 and 2 — a customer who typed their address over several lines
 * already told us where it breaks. City, state and PIN are left for them to fill, because guessing
 * which trailing comma-separated fragment is the city would put a wrong city on an invoice.
 */
export const fromLegacy = (a) => {
  const parts = String((a || {}).address || "").split("\n").map((t) => t.trim()).filter(Boolean);
  return {
    ...emptyAddress(),
    label: s(a, "label"),
    address1: parts[0] || "",
    address2: parts[1] || "",
  };
};

/** What the server will object to, as sentences. Empty when the entry is fine. */
export const problems = (a) => {
  if (!isStructured(a)) return String((a || {}).address || "").trim() ? [] : ["needs an address"];
  const out = REQUIRED.filter((k) => !s(a, k)).map((k) => `needs a ${LABELS[k]}`);
  const pin = s(a, "pincode");
  if (pin && !PINCODE_RE.test(pin)) out.push("needs a six-digit PIN code");
  return out;
};

/** The printable lines: state and PIN share the last one, as a written Indian address does. */
export const addressLines = (a) => {
  if (!isStructured(a)) {
    return String((a || {}).address || "").split("\n").map((t) => t.trim()).filter(Boolean);
  }
  const tail = [s(a, "state"), s(a, "pincode")].filter(Boolean).join(" - ");
  return [s(a, "address1"), s(a, "address2"), s(a, "city"), tail].filter(Boolean);
};

/** One line, for a dropdown option or a table cell. */
export const oneLine = (a) => addressLines(a).join(", ");
